import { NgClass } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { Component, inject } from '@angular/core'
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { DividerModule } from 'primeng/divider'
import { FloatLabelModule } from 'primeng/floatlabel'
import { InputTextModule } from 'primeng/inputtext'
import { PasswordModule } from 'primeng/password'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { InputComponent } from 'app/_shared/components/input/input.component'
import { UserRegister } from 'app/_shared/interfaces/auth'
import { AuthService } from 'app/_shared/services/auth.service'
import { ToastService } from 'app/_shared/services/toast.service'
import {
  passwordMatch,
  passwordValidationLetters,
  passwordValidationNumber
} from 'app/_shared/validators/password-match'
import { emailWithTldValidator } from 'app/_shared/validators/tld-email'
import { AppConfigService } from 'app/app-config.service'
import { UserStore } from 'app/store/user.store'

type ErrorIconKey = 'minLength' | 'number' | 'upperAndLower'

@Component({
  selector: 'app-register',
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    PasswordModule,
    DividerModule,
    FloatLabelModule,
    TranslatePipe,
    FloatLabelModule,
    NgClass,
    ButtonComponent,
    InputComponent
  ],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent {
  authService: AuthService = inject(AuthService)
  userStore = inject(UserStore)
  router = inject(Router)
  activatedRoute = inject(ActivatedRoute)
  toastService = inject(ToastService)
  serverErrors: HttpErrorResponse
  private appConfigService: AppConfigService = inject(AppConfigService)
  errorIcons: Record<ErrorIconKey, string> = {
    minLength: 'error-icon',
    number: 'error-icon',
    upperAndLower: 'error-icon'
  }
  loading: boolean = false

  get storefrontId(): number | null {
    return this.appConfigService.getAppConfig.id
  }

  registerForm = new FormGroup(
    {
      first_name: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      last_name: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      email: new FormControl('', {
        validators: [Validators.required, emailWithTldValidator()],
        nonNullable: true
      }),
      username: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      password: new FormControl('', {
        validators: [
          Validators.required,
          Validators.minLength(8),
          passwordValidationNumber(),
          passwordValidationLetters()
        ],
        nonNullable: true
      }),
      password_confirmation: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      terms_and_conditions: new FormControl(true, { nonNullable: true })
    },
    { validators: passwordMatch() }
  )

  constructor() {
    this.registerForm.controls.password.valueChanges.subscribe(() => {
      const errorMappings: { errorType: string; iconKey: ErrorIconKey }[] = [
        { errorType: 'minlength', iconKey: 'minLength' },
        { errorType: 'atLeastOneNumber', iconKey: 'number' },
        { errorType: 'letterAndSymbol', iconKey: 'upperAndLower' }
      ]
      errorMappings.forEach(({ errorType, iconKey }) => {
        if (errorType === 'minlength') {
          this.errorIcons[iconKey] =
            this.password?.hasError(errorType) || this.password?.hasError('required')
              ? 'error-icon'
              : 'check-icon'
        } else {
          this.errorIcons[iconKey] = this.password?.hasError(errorType)
            ? 'error-icon'
            : 'check-icon'
        }
      })
    })
  }

  get password() {
    return this.registerForm.get('password')
  }

  get confirmPassword() {
    return this.registerForm.get('password_confirmation')
  }

  onSubmit() {
    this.loading = true
    if (this.registerForm.valid) {
      const payload: UserRegister = {
        ...this.registerForm.getRawValue(),
        storefront_id: this.storefrontId
      }

      this.authService.register(payload).subscribe({
        next: res => {
          this.loading = false
          this.userStore.storeUser(res.data.user, res.data.token)
          const returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'] || '/'
          this.router.navigateByUrl(returnUrl)
        },
        error: err => {
          this.loading = false
          this.serverErrors = err.error.errors
          // Extract all error messages from the response
          const errorMessages = Object.values(err.error.errors).flat()
          for (const errorMessage of errorMessages) {
            this.toastService.error(errorMessage as string)
          }
        }
      })
    }
  }
}
